import type {IOnDestroy, IOnInit} from 'angular';
import type {SearchManager} from '../../../search/search-manager';
import type {AnyObject, Component} from 'typings/portaro.fe.types';
import type {ModalWindowActions, ModalWindowBindings, ModalWindowResolveValues} from '../conversation';
import {createPayloadActionResponse} from '../modal-utils';
import type {Observable} from 'rxjs';
import {of, firstValueFrom, Subject} from 'rxjs';
import {map, share, switchMap} from 'rxjs/operators';
import {unwrapPromiseResult, wrapPromiseResult} from 'shared/utils/observables-utils';
import type {SearchManagerFactory} from '../../../search/search-manager.factory';
import type {
    Exemplar,
    Form,
    Intent,
    Loan,
    Record,
    SearchParams,
    StaticSearchParams,
    ViewableExemplar,
    ViewableExemplarWithDocument,
    ViewableSearch
} from 'typings/portaro.be.types';
import bind from 'bind-decorator';
import {Kind, recordTypes} from '../../constants/portaro.constants';
import {someSpecifiedParamsAreEmpty, specifiedParamsHaveChanged} from './search-params-utils';
import {ngAsync} from 'shared/utils/ng-@decorators';
import type {KpUniversalFormSettings} from '../../value-editors/kp-universal-form/types';
import type {ObjectValueEditorField} from '../../value-editors/meta-editors/object/types';
import KpUniversalForm from '../../value-editors/kp-universal-form/KpUniversalForm.svelte';
import type {FormControlOptions} from '../../value-editors/internal/forms/types';
import {defaultUpdateTriggers} from '../../value-editors/internal/forms/form-control';
import {assertIsString} from 'shared/utils/types-utils';

export interface SearchSelectionModalModel {
    resultPostProcessFunction?: SearchSelectionModalModelResultPostprocessFunction;
    staticSearchParams?: StaticSearchParams;
    initialFrontendParams?: AnyObject;
    newRecord?: Record;
    customTemplateUrl?: string;
    createNewValueFn?: () => Promise<Record>;
    selectedItem?: SelectableItem;
    title?: string;
}

export type SearchSelectionModalModelResultPostprocessFunction = ((item: SelectableItem[]) => Observable<SelectableItem[]>);

/**
 * @ngdoc component
 * @name searchSelectionModal
 * @module portaro.conversation
 *
 * @description
 * Search selection component
 */
export default class SearchSelectionModalComponent implements Component<ModalWindowBindings<SearchSelectionModalModel>> {
    public static componentName = 'searchSelectionModal';

    public bindings = {
        resolve: '<'
    } as const;
    public controller = SearchSelectionModalComponentController;
    public templateUrl = require('./search-selection-modal.tpl.ftl');
    public styleUrls = [require('./kp-search-selection-modal.less')]
}

class SearchSelectionModalComponentController implements ModalWindowBindings<SearchSelectionModalModel>, IOnInit, IOnDestroy {

    public kpUniversalFormComponent = KpUniversalForm;
    public formOptionsForDebounce: FormControlOptions = {updateTriggers: [defaultUpdateTriggers.BLUR, {...defaultUpdateTriggers.INPUT, debounceValue: 500}]};

    public resolve: ModalWindowResolveValues<SearchSelectionModalModel>;
    public selectedItem: SelectableItem = null;
    public formSettings: KpUniversalFormSettings<SearchParams>;
    public customTemplateUrl: string;
    public title: string;

    private searchManager: SearchManager<SearchParams, SelectableItem>;
    private model: SearchSelectionModalModel;
    private modalWindowActions: ModalWindowActions;
    private resultPostProcessFunction: SearchSelectionModalModelResultPostprocessFunction;
    private lastSearchParams: SearchParams = {};
    private editableParamsNames: string[];
    private requiredEditableParamNames: string[];
    private formsHaveRequiredParams: boolean;
    private submitRequested = false;
    private purifiedSearchModelFieldsNames: string[];

    /*@ngInject*/
    constructor(private searchManagerFactory: SearchManagerFactory,
                private searchService) {
    }

    @ngAsync()
    public async $onInit(): Promise<void> {
        this.model = this.resolve.model;
        this.modalWindowActions = this.resolve.modalWindowActions;

        this.title = this.resolve.model.title;

        this.resultPostProcessFunction = this.resolve.model.resultPostProcessFunction ?? ((_) => of(_));

        if (this.model.newRecord) { // window is (re)opened with newly created record
            this.select(this.model.newRecord);
        }

        this.searchManager = this.searchManagerFactory.createLocalSearch(
            this.fetchResultsWith((params) => this.searchService.search(params)),
            this.model.staticSearchParams
        )

        if (this.model.initialFrontendParams) {
            await this.searchManager.newSearchWithParams(this.model.initialFrontendParams);
            this.copyLastSearchParams(); // create copy of real params for later comparison
        } else {
            await this.searchManager.loadForms()
        }

        const fields = this.getForms().flatMap((form) => form.fields);

        this.editableParamsNames = fields.map((field) => field.fieldName);

        this.requiredEditableParamNames = fields
            .filter((field) => field.editor.validations?.required)
            .map((field) => field.fieldName);

        this.formsHaveRequiredParams = this.requiredEditableParamNames.length > 0;

        this.selectedItem = this.model?.selectedItem;

        this.formSettings = {
            fields: this.getForms().reduce<ObjectValueEditorField<any>[]>((accumulator, currentForm) => {
                return accumulator.concat(currentForm.fields)
            }, [])
        };

        this.purifiedSearchModelFieldsNames = this.formSettings.fields.map((field) => {
            assertIsString(field.fieldName);
            return field.fieldName;
        });
        this.purifiedSearchModelFieldsNames = this.purifiedSearchModelFieldsNames.concat(Object.keys(this.model.initialFrontendParams ?? {}));

        this.customTemplateUrl = this.model.customTemplateUrl;
    }

    public $onDestroy(): void {
        this.searchManager.destroy();
    }

    public close() {
        this.modalWindowActions.cancel();
    }

    @bind
    public select(item: SelectableItem) {
        this.selectedItem = item;
        this.submitSelectedItem();
    }

    public tryToSubmit() {
        if (this.itemIsSelected() && !this.submitItemOnSelect()) {
            this.submitSelectedItem();
        } else {
            this.submitRequested = true;
            this.requestSearch();
        }
    }

    public async requestSearch() {

        this.purifySearchModel();

        if (this.paramsHaveNotChanged()) {
            return;
        }

        this.copyLastSearchParams(); // create copy of real params for later comparison

        this.selectedItem = null;

        // do not search if current search params are invalid
        if (this.currentParamsAreInvalid()) {
            this.resetSearch();
            return;
        }

        await this.search();

        // reset search if incoming results are no longer needed, because current search params are invalid
        if (this.currentParamsAreInvalid()) {
            this.resetSearch();
            this.resetSearchParams();
            return;
        }

        this.copyLastSearchParams(); // create copy of real params for later comparison
    }

    public createNew() {
        const promise = this.model.createNewValueFn().then((editedRecord) => {
            this.model.newRecord = editedRecord;
            return this.model;
        });
        this.modalWindowActions.reopen(promise);
    }

    public canCreateNew(): boolean {
        if (this.searchManager) {
            return this.model.createNewValueFn && !!this.searchManager.lastResult();
        }
        return !!this.model.createNewValueFn;
    }

    public canShowResults(): boolean {
        return true;
    }

    public canShowForms(): boolean {
        return this.searchManager.getForms().length > 0;
    }

    public getForms(): Form[] {
        return this.searchManager.getForms();
    }

    public getPagination(): any {
        return this.searchManager.getPagination();
    }

    public isResultEmpty(): boolean {
        return this.searchManager.lastResult().totalElements === 0;
    }

    public getAvailableIntents(): Intent[] {
        return this.searchManager.lastSearch()?.availableIntents;
    }

    @bind
    public newSearchWithParams(newParams: SearchParams): void {
        this.searchManager.newSearchWithParams(newParams);
    }

    public isRecord(item: any): boolean {
        return item.type === recordTypes.TYPE_AUTHORITY || item.type === recordTypes.TYPE_DOCUMENT;
    }

    public isExemplar(item: any): boolean {
        return this.model.staticSearchParams.kind === Kind.KIND_EXEMPLAR;
    }

    public showQuantityInput(): boolean {
        if (!this.itemIsSelected()) {
            return false;
        }

        if (!this.isExemplar(this.selectedItem)) {
            return false;
        }

        return this.getSelectedItem<ViewableExemplar>().loanCategory.chunkable;
    }

    public submitItemOnSelect(): boolean {
        return Object.values(recordTypes).includes(this.model.staticSearchParams.kind);
    }

    /**
     * Remove all fields which is not in search form and initialFrontendParams
     *
     * @private
     */
    private purifySearchModel() {
        const params = this.searchManager.params();

        for (const param in params) {
            if (Object.hasOwn(params, param) &&
                !this.purifiedSearchModelFieldsNames.includes(param)) {
                params[param] = undefined;
                delete params[param];
            }
        }
    }

    // TODO: remove this hack after major search refactoring
    private mapSearchContent<T, K>(search: ViewableSearch<SearchParams, T>, mapFn: ((_: T[]) => Observable<K[]>)): Observable<ViewableSearch<SearchParams, K>> {

        const oldContent = search.result.content;
        search.result.content = null;
        const newSearch = (search as unknown) as ViewableSearch<SearchParams, K>;

        return mapFn(oldContent).pipe(map((newContent) => {
            newSearch.result.content = newContent;
            return newSearch;
        }));
    }

    // TODO: remove this hack after major search refactoring
    @bind
    private fetchResultsWith(queryFunction: QueryFn): QueryFn {

        const searchQuery$ = new Subject<SearchParams>();
        const searchResult$ =
            searchQuery$.pipe(
                switchMap((queryParams) => wrapPromiseResult(queryFunction(queryParams))),
                switchMap((wrapper) => { // un-wrap

                    if (wrapper.error) {
                        return of(wrapper) // re-wrap
                    }

                    return this.mapSearchContent(wrapper.data, this.resultPostProcessFunction)
                               .pipe(map((data) => ({data}))); // re-wrap
                }),
                share());

        searchResult$.subscribe();

        return (params: SearchParams) => {
            searchQuery$.next(params);
            return firstValueFrom(searchResult$.pipe(unwrapPromiseResult()));
        }
    }

    private async search() {

        await this.searchManager.newSearch();

        const result = this.searchManager.lastResult();
        const isSingleResult = result.totalElements === 1 && result.content.length === 1;

        if (isSingleResult) {
            this.selectedItem = this.searchManager.lastSearch().result.content[0];
        }

        if (this.submitRequested && isSingleResult && !this.submitItemOnSelect()) {
            this.submitSelectedItem();
        }
    }

    private isSubmittable(): boolean {
        if (!this.itemIsSelected()) {
            return false;
        }

        if (this.submitItemOnSelect()) {
            return true;
        }

        if (this.isExemplar(this.selectedItem)) {
            const exemplar = this.getSelectedItem<ViewableExemplar>()
            return !exemplar.loanCategory.chunkable || exemplar.quantity > 0;
        }

        return true;
    }

    private submitSelectedItem(): void {
        if (this.isSubmittable()) {
            this.modalWindowActions.submitPromise(Promise.resolve(createPayloadActionResponse(this.selectedItem)));
        }
    }

    private getSelectedItem<T extends SelectableItem>(): T {
        return this.selectedItem as T;
    }

    private itemIsSelected(): boolean {
        return !!this.selectedItem;
    }

    private currentParamsAreInvalid() {
        return this.formsHaveRequiredParams && someSpecifiedParamsAreEmpty(this.lastSearchParams, this.requiredEditableParamNames);
    }

    private paramsHaveNotChanged(): boolean {
        return !specifiedParamsHaveChanged(this.lastSearchParams, this.searchManager.params(), this.editableParamsNames);
    }

    private copyLastSearchParams() {
        this.lastSearchParams = {...this.searchManager.params()};
    }

    private resetSearchParams() {
        this.searchManager.setParams({...this.lastSearchParams});
    }

    private resetSearch() {
        this.searchManager.reset();
        this.submitRequested = false;
    }

    public get searchModel() {
        return this.searchManager.params();
    }

    public set searchModel(model: SearchParams) {
        for (const param in model) {
            if (Object.hasOwn(model, param)) {
                this.searchManager.params()[param] = model[param];
            }
        }
    }
}

type SelectableItem = Exemplar | Record | ViewableExemplar | ViewableExemplarWithDocument | Loan;

type QueryFn = ((params: SearchParams) => Promise<ViewableSearch<SearchParams, SelectableItem>>);
